//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import trackSourceButtonTemplate from '@legacy-components/common/universal-source-actions/track-source-button.html';

import _ from "js/underscore-mixins";

// Depends on moment.js library.
(function () {
  const curalate = angular.module('curalate');

  curalate.component('trackSourceButton', {
    template: trackSourceButtonTemplate,
    bindings: {
      sourceType: '<',
      sourceId: '<',
      sourceName: '<',
      secondarySourceId: '<',
      trackingCategory: '<',
      onTrack: '&?',
      onUntrack: '&?',
      isDisabled: '<?',
      isIgBusinessAccountConnected: '<',
    },
    transclude: {
      trackLink: '?trackLink',
    },
    controller: [
      '$modal',
      '$q',
      'CollectionsService',
      'ContentSourceTypes',
      'ErrorNotificationService',
      'PermissionsService',
      'SessionVarsService',
      'TrackedSourcesApiService',
      'TrackedSourcesService',
      'TrackingService',
      function (
        $modal,
        $q,
        CollectionsService,
        ContentSourceTypes,
        ErrorNotificationService,
        PermissionsService,
        SessionVarsService,
        TrackedSourcesApiService,
        TrackedSourcesService,
        TrackingService,
      ) {
        const self = this;

        let trackSource;
        let untrackSource;
        let untrackModal;
        let buildUntrackWarningText;

        self.trackAction = TrackingService.ACTION_EXPLORE_TRACK_SOURCE;
        self.untrackAction = TrackingService.ACTION_EXPLORE_UNTRACK_SOURCE;
        self.isDisabledForMissingGraphApiAccess = false;

        this.$onInit = function () {
          self.isTracking = false;
          self.asyncOutstanding = true;

          TrackedSourcesApiService.getTrackedSources(self.sourceType).then(
            (sources) => {
              if (_(sources).isArray()) {
                self.source = sources.find((source) => {
                  return (
                    source.name === self.sourceName ||
                    source.id === self.sourceId
                  );
                });
              }

              self.isTracked = !!self.source;

              // Disable track button if source is a brand account
              if (
                self.isTracked &&
                _(self.source).safeGet('userDetails.isBrandOwned')
              ) {
                self.tooltipText =
                  'You cannot untrack a brand-owned Instagram account.';
                self.isBrandOwned = true;
              } else {
                self.tooltipText =
                  "You don't have permission to take this action. Contact a group admin if this is a mistake.";
                self.isBrandOwned = false;
              }
              self.asyncOutstanding = false;
            },
          );

          self.trackClick = () => {
            if (!self.shouldButtonBeDisabled()) {
              if (self.isTracked) {
                untrackModal();
              } else {
                trackSource();
              }
            }
          };
        };

        this.$onChanges = function (changesObj) {
          if (changesObj.isIgBusinessAccountConnected) {
            self.isDisabledForMissingGraphApiAccess =
              self.sourceType === ContentSourceTypes.BUSINESS_USER &&
              !self.isIgBusinessAccountConnected;
          }
        };

        self.shouldButtonBeDisabled = () => {
          return (
            self.hasError ||
            self.asyncOutstanding ||
            self.isBrandOwned ||
            (self.isDisabledForMissingGraphApiAccess && !self.isTracked)
          );
        };

        trackSource = function () {
          if (!self.isTracked) {
            self.asyncOutstanding = true;
            self.isTracking = true;
            return TrackedSourcesApiService.addTrackedSource({
              sourceType: self.sourceType,
              sourceId: self.sourceId,
              secondarySourceId: self.secondarySourceId,
            })
              .then(() => {
                TrackedSourcesService.refresh();

                if (_(self.onTrack).isFunction()) {
                  self.onTrack();
                }
                self.isTracked = true;
              })
              .catch((error) => {
                if (error.startsWith('TrackLimit::')) {
                  ErrorNotificationService.showTrackLimitErrorDialog(error);
                } else {
                  ErrorNotificationService.showDefaultErrorDialog(error);
                }
              })
              .finally(() => {
                self.asyncOutstanding = false;
                self.isTracking = false;
              });
          }

          return $q.resolve(true);
        };

        untrackModal = function () {
          return $modal.open({
            template:
              '<confirmation-modal close="$close(result)" ' +
              'confirm-text="confirmText" ' +
              'modal-title="modalTitle" ' +
              'submit-async="true" ' +
              'submit-action="submitAction(result)">' +
              '<p><span class="text-size-medium">' +
              '{{ warningText() }}' +
              '</span></p>' +
              '</confirmation-modal>',
            windowClass: 'pure-bs3 content-detail-modal',
            controller: [
              '$scope',
              'submitAction',
              'sourceName',
              'warningText',
              function ($modalScope, submitAction, sourceName, warningText) {
                $modalScope.modalTitle = `Stop Tracking: ${sourceName}`;
                $modalScope.confirmText = 'Yes, Stop Tracking';
                $modalScope.submitAction = submitAction;
                $modalScope.warningText = warningText;
              },
            ],
            resolve: {
              submitAction() {
                return untrackSource;
              },
              sourceName() {
                return self.source.name;
              },
              warningText() {
                return buildUntrackWarningText;
              },
            },
          });
        };

        untrackSource = function () {
          if (self.isTracked) {
            self.asyncOutstanding = true;
            TrackingService.recordEvent(
              self.trackingCategory,
              TrackingService.ACTION_UNTRACK_CONFIRMED,
            );
            return TrackedSourcesApiService.removeTrackedSource(
              self.sourceId,
              self.sourceType,
            )
              .then(() => {
                self.isTracked = false;
                self.asyncOutstanding = false;

                TrackedSourcesService.refresh();

                if (_(self.onUntrack).isFunction()) {
                  self.onUntrack();
                }
              })
              .then(() => {
                CollectionsService.refreshCollections();
              })
              .catch((response) => {
                const errMsg =
                  response ||
                  `Sorry, we encountered an error when removing this ${self.sourceType}.`;
                ErrorNotificationService.showDefaultErrorDialog(errMsg);

                self.asyncOutstanding = false;
              });
          }

          return $q.resolve(true);
        };

        buildUntrackWarningText = function () {
          const additionalCollections = SessionVarsService.isFeatureEnabled(
            'galleries',
          )
            ? 'Galleries'
            : 'Fanreels';

          const warningText =
            'Are you sure you want to stop tracking this source? ' +
            'There is a possibility you will lose data if you decide to track it again in the future. ' +
            `Untracking this source will also remove it from all Collections, Lists, and ${additionalCollections} it is included in.`;

          return warningText;
        };
      },
    ],
  });
})();
